import type { Edge, Node } from '@xyflow/react';
import {
  isValidConnection as isAcyclicWorkflowConnection,
  isDownstream,
} from 'src/lib/workflows/graph-utils';
import { getSocketTypeFromNode } from 'src/lib/workflows/editorUtils';
import { explainSocketTypeIncompatibility } from 'src/lib/workflows/areSocketTypesCompatible';

/** Minimal connection shape from React Flow `Handle` `isValidConnection` / `onConnect`. */
export type WorkflowConnectionLike = {
  source?: string | null;
  target?: string | null;
  sourceHandle?: string | null;
  targetHandle?: string | null;
};

/** Drag origin / hover target for explaining blocked connections during wire drag. */
export type ConnectionDragEndpoint = {
  nodeId: string;
  handleId: string;
  handleType: 'source' | 'target';
  socketType: string;
};

/**
 * If this output→input connection is invalid, a short reason for toasts; otherwise `null`.
 */
export function getWorkflowSocketConnectionRejectionReason(
  connection: WorkflowConnectionLike,
  getNode: (id: string) => Node | undefined,
  getEdges: () => Edge[],
): string | null {
  const { source, target, sourceHandle, targetHandle } = connection;
  if (!source || !target || !sourceHandle || !targetHandle) {
    return null;
  }

  if (!isAcyclicWorkflowConnection(source, target, getEdges())) {
    return 'That link would create a cycle. Keep data flowing in one direction through the graph.';
  }

  const sourceNode = getNode(source);
  const targetNode = getNode(target);
  if (!sourceNode || !targetNode) {
    return 'Could not resolve one of these steps—try refreshing the page.';
  }

  const sourceSocketType = getSocketTypeFromNode(
    sourceNode,
    sourceHandle,
    'output',
  );
  const targetSocketType = getSocketTypeFromNode(
    targetNode,
    targetHandle,
    'input',
  );

  return explainSocketTypeIncompatibility(sourceSocketType, targetSocketType);
}

/**
 * Whether React Flow should allow completing a connection between two data handles.
 * Enforces DAG acyclicity (same as save-time validation) and output→input socket types.
 */
export function isWorkflowSocketConnectionValid(
  connection: WorkflowConnectionLike,
  getNode: (id: string) => Node | undefined,
  getEdges: () => Edge[],
): boolean {
  return (
    getWorkflowSocketConnectionRejectionReason(
      connection,
      getNode,
      getEdges,
    ) === null
  );
}

/**
 * Human-readable reason a wire from `drag` to `hover` would be rejected (while dragging).
 */
export function getConnectionDragHoverRejectionReason(
  drag: ConnectionDragEndpoint,
  hover: ConnectionDragEndpoint,
  getNode: (id: string) => Node | undefined,
  getEdges: () => Edge[],
): string | null {
  if (drag.nodeId === hover.nodeId) {
    return "You can't connect a step to itself.";
  }

  if (drag.handleType === hover.handleType) {
    return 'Connect an output into an input, or drag from an input back to an output—not two of the same kind.';
  }

  if (
    drag.handleType === 'target' &&
    hover.handleType === 'source' &&
    isDownstream(drag.nodeId, hover.nodeId, getEdges())
  ) {
    return "Data can't run backward—use an earlier step's output or a different socket.";
  }

  const connection: WorkflowConnectionLike =
    drag.handleType === 'source'
      ? {
          source: drag.nodeId,
          sourceHandle: drag.handleId,
          target: hover.nodeId,
          targetHandle: hover.handleId,
        }
      : {
          source: hover.nodeId,
          sourceHandle: hover.handleId,
          target: drag.nodeId,
          targetHandle: drag.handleId,
        };

  return getWorkflowSocketConnectionRejectionReason(
    connection,
    getNode,
    getEdges,
  );
}
